/* EnumType.js */

"use strict";

const Uuid = require("uuid");

/**
 * @type {{Control:{Textfield:{InputText:InputTextControlComponent}, Boolean:{InputCheckbox:InputCheckboxControlComponent}}}}
 */
const uiCore = require("@gluenetworks/steve_ui_core");
const InputTextControl = uiCore.Control.Textfield.InputText;
const InputCheckboxControl = uiCore.Control.Boolean.InputCheckbox;

class EnumType {
    /**
     * @typedef {{ids: string[], labels: string[], [multiSelect]: boolean}} EnumTypeInfo  - enum type info object
     */

    /**
     *
     * @param options
     */
    constructor(options) {
        this.isNewItem = Boolean(options.item.isNew);

        this.gridInstance = options.grid;
        this.cancelChanges = options.cancelChanges;
        this.commitChanges = options.commitChanges;

        /**
         * @type {?EnumTypeInfo}
         * @private
         */
        this._initialData = {};

        /**
         * @type {Array.<{id:string, label:string}>} items data
         */
        this.itemsData = [];

        /**
         * @type {HTMLDivElement}
         */
        this.rootElem = this._createRootElement();

        /**
         * @type {HTMLUListElement}
         */
        this.enumList = this._createList();

        /**
         * @type {HTMLElement}
         */
        this.addBtn = this._createAddBtn();

        /**
         * @type {InputTextControlComponent}
         */
        this.input = this._createInput();

        /**
         * @type {InputCheckboxControlComponent}
         */
        this.multiSelectCheckbox = this._createCheckbox({
            label: "is multiple",
            disabled: true //[!this.isNewItem]  disable multi-select as it hasn't been in specification. Uncomment when its time  comes.
        });

        this._registerListeners();
    }

    get rootElement() {
        return this.getRoot();
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */

    /*******************************************/

    _registerListeners() {
        this.rootElem.addEventListener("click", (this._onRootClick = this._onRootClick.bind(this)));
        this.rootElem.addEventListener(
            "keydown",
            (this._onRootKeyDown = this._onRootKeyDown.bind(this))
        );
        this.input.addListener(
            "UserValue",
            (this._onInputUserValue = this._onInputUserValue.bind(this))
        );
        this.multiSelectCheckbox.on(
            "UserValue",
            (this._onMultipleChange = this._onMultipleChange.bind(this))
        );
        this.addBtn.addEventListener("click", (this._onUserAdd = this._onUserAdd.bind(this)));
    }

    _unregisterListeners() {
        this.rootElem.removeEventListener("click", this._onRootClick);
        this.rootElem.removeEventListener("keydown", this._onRootKeyDown);
        this.input.removeListener("UserValue", this._onInputUserValue);
        this.multiSelectCheckbox.removeListener("UserValue", this._onMultipleChange);
        this.addBtn.removeEventListener("click", this._onUserAdd);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */

    /*******************************************/

    getRoot() {
        return this.rootElem;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    _onRootClick(e) {
        e.stopPropagation();
        const root = e.currentTarget;
        const target = e.target;

        if (target.classList.contains("delete-btn")) {
            this._removeItem(this._getEnumListItem(target, root));
        }
        if (target.classList.contains("edit-btn")) {
            this._editItem(this._getEnumListItem(target, root));
        }
        if (target.classList.contains("submit-btn")) {
            this._submitItem(this._getEnumListItem(target, root));
        }

    }

    _onRootKeyDown(e) {
        e.stopPropagation();
        const listItem = this._getEnumListItem(e.target, this.getRoot());
        if (e.key === "Enter" || e.which === 13) {
            //Enter is pressed
            if (e.target.value && !listItem) {
                this._addValue(e.target.value);
                this.enumList.scrollTop = this.enumList.scrollHeight;
                e.target.value = "";
            }
            if (e.ctrlKey || e.metaKey) {
                this.commitChanges();
            }

            if (e.target.classList.contains("text") && listItem) {
                this._submitItem(listItem);
            }
        }

        if (e.key === "Escape" || e.which === 27) {
            if (listItem) {
                this._cancelItem(listItem);
            } else {
                this.cancelChanges();
            }
        }
    }

    _getEnumListItem(target, root) {
        if (!target || target === root) {
            return null;
        }
        if (target.classList.contains("list-item")) {
            return target;
        }
        return this._getEnumListItem(target.parentNode, root);
    }

    _onInputUserValue(value) {
        this.addBtn.classList.toggle("disabled", !this._valueIsValid(value));
    }

    _onUserAdd() {
        const value = this.input.getValue();
        if (this._valueIsValid(value)) {
            this._addValue(value);
            this.enumList.scrollTop = this.enumList.scrollHeight;
            this.input.setValue("");
        }
        this._focus();
    }

    _onMultipleChange(/*state*/) {}
    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */

    /*******************************************/

    _shutdown() {
        this._unregisterListeners();
        // /*?*/ this.rootElem.remove();

        return Promise.all([this.input.shutdown(), this.multiSelectCheckbox.shutdown()])
            .then(() => {
                this.addBtn.remove();
                this.enumList.remove();
                this.rootElem.remove();
            })
            .then(() => {
                this._initialData = null;
                this.itemsData = null;
                this.input = null;
                this.multiSelectCheckbox = null;
                this.addBtn = null;
                this.enumList = null;
                this.rootElem = null;
            });
    }

    _render() {
        this.rootElem.innerHTML = "";

        const inputWrapper = document.createElement("div");
        inputWrapper.classList.add("input-wrapper");

        inputWrapper.appendChild(this.input.renderer.rootElement);
        inputWrapper.appendChild(this.addBtn);

        if (!this.multiSelectCheckbox.isDisabled() || this.multiSelectCheckbox.getValue()) {
            this.rootElem.appendChild(this.multiSelectCheckbox.renderer._rootElem);
        }
        this.rootElem.appendChild(this.enumList);
        this.rootElem.appendChild(inputWrapper);
    }

    /**
     *
     * @returns {string}
     * @private
     */
    _generateItemId() {
        return Uuid.v4();
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PRIVATE RENDER METHODS             */

    /********************************************/

    /**
     *
     * @returns {HTMLDivElement}
     * @private
     */
    _createRootElement() {
        const rootElem = document.createElement("div");
        rootElem.classList.add("enum-editor", "inner-editor");
        rootElem.component = this;
        return rootElem;
    }

    /**
     *
     * @returns {HTMLUListElement}
     * @private
     */
    _createList() {
        const enumList = document.createElement("ul");
        enumList.classList.add("enum-list");
        return enumList;
    }

    /**
     *
     * @param {Object} itemValue
     * @param {string} itemValue.label
     * @param {string} itemValue.id
     * @returns {HTMLLIElement}
     * @private
     */
    _createListItem(itemValue) {
        const { label: text, id } = itemValue;
        const listItem = document.createElement("li");
        listItem.classList.add("list-item");
        listItem.setAttribute("title", text);
        listItem.dataset.id = id;
        listItem.dataset.label = text;

        const enumTitle = document.createElement("span");
        enumTitle.classList.add("text");
        enumTitle.innerText = text;

        listItem.append(enumTitle);
        listItem.append(this._createDeleteBtn(text));
        listItem.append(this._createEditBtn(text));

        return listItem;
    }

    _createEditingListItem(itemValue) {
        const { label: text, id } = itemValue;
        const listItem = document.createElement("li");
        listItem.classList.add("list-item");
        listItem.setAttribute("title", text);
        listItem.dataset.id = id;
        listItem.dataset.label = text;

        const enumInput = document.createElement("input");
        enumInput.classList.add("text");
        enumInput.value = text;

        listItem.append(enumInput);
        listItem.append(this._createSubmitBtn(text));

        return listItem;
    }

    _createInput() {
        return new InputTextControl({ darkTheme: true });
    }

    _createAddBtn() {
        const checkMark = document.createElement("base-icon-gl");
        checkMark.dataset.icon = "common.check";
        checkMark.setDarkTheme(true);
        checkMark.setAttribute("role", "button");
        checkMark.classList.add("disabled");
        return checkMark;
    }

    _createDeleteBtn(title) {
        const deleteEnumBtn = document.createElement("button");
        deleteEnumBtn.classList.add("delete-btn");
        deleteEnumBtn.setAttribute("title", `Delete ${title}`);
        deleteEnumBtn.innerHTML = "&#x2716;";
        return deleteEnumBtn;
    }

    _createEditBtn(title) {
        const editEnumBtn = document.createElement("button");
        editEnumBtn.classList.add("edit-btn");
        editEnumBtn.setAttribute("title", `Edit ${title}`);
        editEnumBtn.innerHTML = "&#x270e;";
        return editEnumBtn;
    }

    _createSubmitBtn(title) {
        const submitEnumBtn = document.createElement("button");
        submitEnumBtn.classList.add("submit-btn");
        submitEnumBtn.setAttribute("title", `Submit ${title}`);
        submitEnumBtn.innerHTML = "&#x2713;";
        return submitEnumBtn;
    }

    _createCheckbox(options) {
        return new InputCheckboxControl(
            Object.assign(
                {
                    labelShown: true,
                    value: false,
                    darkTheme: true
                },
                options
            )
        );
    }

    /********************************************/
    /* END PRIVATE RENDER METHODS               */
    /* START PRIVATE API METHODS                */

    /********************************************/

    /**
     *
     * @param {EnumTypeInfo} data
     * @private
     */
    _resetData(data) {
        this.data = {};
        this.itemsData = [];
        this._initialData = data;

        this._setData(data);
    }

    /**
     *
     * @param {EnumTypeInfo} data
     * @private
     */
    _setData(data) {
        const { ids = [], labels = [], multiSelect } = data;
        this.multiSelectCheckbox.setValue(Boolean(multiSelect));

        for (let idx = 0; idx < ids.length; idx++) {
            this._addValue(labels[idx], ids[idx]);
        }
    }

    /**
     *
     * @returns {EnumTypeInfo}
     */
    _getData() {
        const { ids, labels } = this.itemsData.reduce(
            (acc, { id, label }) => {
                acc.ids.push(id);
                acc.labels.push(label);
                return Object.assign({}, acc);
            },
            { ids: [], labels: [] }
        );

        return {
            ids,
            labels,
            multiSelect: this.multiSelectCheckbox.getValue()
        };
    }

    /**
     *
     * @param {string} label - item label
     * @param {string}  [id] - item id in uuid format
     * @private
     */
    _addValue(label, id) {
        if (this._valueIsValid(label)) {
            id = id || this._generateItemId();

            const value = { label, id };
            this.itemsData.push(value);
            this.enumList.appendChild(this._createListItem(value));
        }
    }

    /**
     *
     * @param {HTMLLIElement} item -
     * @private
     */
    _removeItem(item) {
        this.itemsData.splice(this.itemsData.findIndex(({ id }) => id === item.dataset.id), 1);
        item.remove();
    }

    _editItem(item) {
        const itemValue = this.itemsData.find(({ id }) => id === item.dataset.id);
        const editingListItem = this._createEditingListItem(itemValue);

        item.replaceWith(editingListItem);
        editingListItem.querySelector("input").focus();
    }

    _submitItem(item) {
        if(item) {
            const itemValue = this.itemsData.find(({ id }) => id === item.dataset.id);
            itemValue.label = item.querySelector("input").value;
            item.replaceWith(this._createListItem(itemValue));
        }
    }

    _cancelItem(item) {
        if (item) {
            const itemValue = this.itemsData.find(({ id }) => id === item.dataset.id);
            item.replaceWith(this._createListItem(itemValue));
        }
    }

    _focus() {
        this.input.focus();
    }

    /**
     * checks if new label is valid
     * @param value
     * @returns {boolean}
     * @private
     */
    _valueIsValid(value) {
        return Boolean(value) && !this.itemsData.find(({ label }) => label === value);
    }
    /********************************************/
    /*  END PRIVATE API METHODS                 */
    /* START PUBLIC API METHODS */

    /*******************************************/

    destroy() {
        this._shutdown().catch(console.error);
    }

    /**
     *
     * @param {EnumTypeInfo} enumTypeInfo - enum type info object
     */
    setData(enumTypeInfo) {
        const data = enumTypeInfo || {};
        this._resetData(data);
        this._render();
        this._focus();
    }

    /**
     *
     * @returns {EnumTypeInfo}
     */
    getData() {
        return this._getData();
    }

    isValueChanged() {
        return JSON.stringify(this._initialData) !== JSON.stringify(this._getData());
    }

    validate() {
        return {
            valid: true,
            msg: null
        };
    }

    focus() {
        this._focus();
    }

    /********************************************/
    /* END PUBLIC API METHODS                   */
    /********************************************/
}

module.exports = EnumType;
